import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    XMarkIcon,
    CheckIcon,
    PencilIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
    updateTemplate,
    updateTemplateStage,
} from "../services/adminSellFunnel";
const CriterionDetails = ({ criterion }) => {
    if (!criterion) return null;

    const isAI = criterion.type === "AI";

    return (
        <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-4">

            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">
                        جزئیات معیار
                    </span>

                    <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${isAI
                            ? "bg-purple-100 text-purple-700"
                            : "bg-blue-100 text-blue-700"
                            }`}
                    >
                        {isAI ? "AI" : "قانون سیستمی"}
                    </span>
                </div>
            </div>

            {/* Title */}
            <div className="mb-3">
                <div className="text-xs text-gray-500 mb-1">
                    عنوان معیار
                </div>

                <div className="text-sm font-medium text-gray-800">
                    {criterion.title || "-"}
                </div>
            </div>

            {/* Description */}
            <div className="mb-3">
                <div className="text-xs text-gray-500 mb-1">
                    توضیحات
                </div>

                <div className="text-sm text-gray-700 leading-6">
                    {criterion.description || "توضیحی ثبت نشده است"}
                </div>
            </div>

            {/* AI Prompt */}
            {isAI && criterion.prompt && (
                <div className="mb-3">
                    <div className="text-xs text-gray-500 mb-1">
                        Prompt
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 leading-6">
                        {criterion.prompt}
                    </div>
                </div>
            )}

            {/* System Rule */}
            {!isAI && criterion.ruleConfig && (
                <div className="mb-3">
                    <div className="text-xs text-gray-500 mb-2">
                        تنظیمات قانون
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

                            <div>
                                <div className="text-xs text-gray-500 mb-1">
                                    فیلد
                                </div>

                                <div className="text-sm font-medium text-gray-800">
                                    {criterion.ruleConfig.field || "-"}
                                </div>
                            </div>

                            <div>
                                <div className="text-xs text-gray-500 mb-1">
                                    عملگر
                                </div>

                                <div className="text-sm font-medium text-gray-800">
                                    {criterion.ruleConfig.operator || "-"}
                                </div>
                            </div>

                            <div>
                                <div className="text-xs text-gray-500 mb-1">
                                    مقدار
                                </div>

                                <div className="text-sm font-medium text-gray-800">
                                    {criterion.ruleConfig.value ?? "-"}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* Usage */}
            {criterion.usage && (
                <div>
                    <div className="text-xs text-gray-500 mb-2">
                        میزان استفاده
                    </div>

                    <div className="flex flex-wrap gap-2">

                        <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                            <span className="text-xs text-gray-500">
                                استفاده در قالب‌ها:
                            </span>

                            <span className="mr-1 text-sm font-semibold text-gray-800">
                                {criterion.usage.templateUsageCount ?? 0}
                            </span>
                        </div>

                        <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                            <span className="text-xs text-gray-500">
                                قیف‌های شخصی:
                            </span>

                            <span className="mr-1 text-sm font-semibold text-gray-800">
                                {criterion.usage.personalizedFunnelUsageCount ?? 0}
                            </span>
                        </div>

                        <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                            <span className="text-xs text-gray-500">
                                مجموع استفاده:
                            </span>

                            <span className="mr-1 text-sm font-semibold text-gray-800">
                                {criterion.usage.totalUsageCount ?? 0}
                            </span>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};
const EditTemplateModal = ({
    isOpen,
    onClose,
    template,
    criteriaData,
    onSave,
}) => {
    const accessToken = useSelector((state) => state.auth?.accessToken);

    // ============================
    // States
    // ============================
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [stages, setStages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showCriterionDetails, setShowCriterionDetails] = useState({});
    // ============================
    // Track changed stages for batch update
    // ============================
    const [changedStages, setChangedStages] = useState({});

    // ============================
    // Initialize Form
    // ============================
    useEffect(() => {
        if (template && isOpen) {
            setName(template.name || "");
            setCategory(template.businessCategory || "");
            setStages(
                template.stages?.map((stage) => ({
                    ...stage,
                    criterionId: stage.criterionId || "",
                })) || []
            );
            setChangedStages({});
            setError("");
            setSuccessMessage("");
        }
    }, [template, isOpen]);

    // ============================
    // Get Criteria by Stage Order
    // ============================
    const getCriteriaByStage = (stageOrder) => {
        return criteriaData.filter(
            (item) => Number(item.stageOrder) === stageOrder &&
                item.isActive === true &&
                item.isVisible === true
        );
    };

    // ============================
    // Get Selected Criterion Details
    // ============================
    const getSelectedCriterion = (stageOrder) => {
        const criteriaList = getCriteriaByStage(stageOrder);
        const stage = stages.find((s) => s.stageOrder === stageOrder);
        return criteriaList.find((c) => c.id === stage?.criterionId) || null;
    };
    const toggleCriterionDetails = (stageOrder) => {
        setShowCriterionDetails((prev) => ({
            ...prev,
            [stageOrder]: !prev[stageOrder],
        }));
    };
    // ============================
    // Handle Stage Criterion Change
    // ============================
    const handleCriterionChange = (stageOrder, criterionId) => {
        const originalStage = template.stages?.find(
            (s) => s.stageOrder === stageOrder
        );

        // اگر معیار تغییر کرده، در لیست تغییرات ثبت کن
        if (originalStage?.criterionId !== criterionId) {
            setChangedStages((prev) => ({
                ...prev,
                [stageOrder]: criterionId,
            }));
        } else {
            // اگر به همان مقدار قبلی برگشت، از لیست تغییرات حذف کن
            const newChanged = { ...changedStages };
            delete newChanged[stageOrder];
            setChangedStages(newChanged);
        }

        setStages((prev) =>
            prev.map((stage) =>
                stage.stageOrder === stageOrder
                    ? { ...stage, criterionId }
                    : stage
            )
        );
    };

    // ============================
    // Save All Changes
    // ============================
    const handleSave = async () => {
        if (!name.trim()) {
            setError("نام قیف را وارد کنید.");
            return;
        }

        // بررسی اینکه همه مراحل معیار داشته باشند
        const emptyStages = stages.filter((s) => !s.criterionId);
        if (emptyStages.length > 0) {
            setError(
                `لطفاً برای همه مراحل معیار انتخاب کنید. (مراحل ${emptyStages
                    .map((s) => s.stageOrder)
                    .join(", ")} خالی هستند)`
            );
            return;
        }

        setLoading(true);
        setError("");
        setSuccessMessage("");

        try {
            // 1. به‌روزرسانی متادیتا (نام و دسته‌بندی)
            await updateTemplate(
                template.id,
                {
                    name: name.trim(),
                    businessCategory: category.trim() || "general",
                },
                accessToken
            );

            // 2. به‌روزرسانی معیارهای تغییر کرده (هر مرحله جداگانه)
            const changedStageEntries = Object.entries(changedStages);
            for (const [stageOrder, criterionId] of changedStageEntries) {
                await updateTemplateStage(
                    template.id,
                    Number(stageOrder),
                    criterionId,
                    accessToken
                );
            }

            setSuccessMessage("قیف با موفقیت به‌روزرسانی شد.");

            setTimeout(() => {
                if (onSave) onSave();
                onClose();
            }, 1500);

        } catch (err) {
            console.error("UPDATE TEMPLATE ERROR:", err);

            if (err.response?.status === 409) {
                setError(
                    err.response?.data?.message ||
                    "این قیف در حال استفاده است و نمی‌توان آن را ویرایش کرد."
                );
            } else {
                const backendMessage =
                    err?.response?.data?.message ||
                    err?.response?.data?.detail ||
                    "به‌روزرسانی قیف با خطا مواجه شد.";
                setError(backendMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // ============================
    // Get Stage Number in Persian
    // ============================
    const getStageNumber = (stageOrder) => {
        const stageMap = {
            1: "۱",
            2: "۲",
            3: "۳",
            4: "۴",
            5: "۵",
            6: "۶",
        };
        return stageMap[stageOrder] || stageOrder;
    };

    // ============================
    // Get Type Badge
    // ============================
    const getTypeBadge = (type) => {
        if (type === "AI") {
            return (
                <span className="inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                    AI
                </span>
            );
        }
        if (type === "SYSTEM_RULE") {
            return (
                <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    SYSTEM_RULE
                </span>
            );
        }
        return null;
    };

    // ============================
    // Render
    // ============================
    if (!isOpen || !template) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-zinc-800">
                {/* Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-teal-100 bg-white px-6 py-4 dark:border-zinc-700 dark:bg-zinc-800">
                    <h3 className="text-xl font-bold text-zinc-900 dark:text-white">
                        ویرایش قیف آماده
                    </h3>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-zinc-700"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {/* Error/Success Messages */}
                    {error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/30 dark:bg-red-900/20 dark:text-red-400">
                            <div className="flex items-start gap-2">
                                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}
                    {successMessage && (
                        <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700 dark:border-teal-800/30 dark:bg-teal-900/20 dark:text-teal-400">
                            <div className="flex items-start gap-2">
                                <CheckIcon className="h-5 w-5 flex-shrink-0 text-teal-500 dark:text-teal-400" />
                                <span>{successMessage}</span>
                            </div>
                        </div>
                    )}

                    {/* General Information */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                نام قیف
                                <span className="mr-1 text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white dark:focus:border-teal-400"
                                placeholder="نام قیف را وارد کنید"
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                                دسته‌بندی نمایشی
                            </label>
                            <input
                                type="text"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                placeholder="مثلاً e-commerce"
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white dark:focus:border-teal-400"
                            />
                        </div>
                    </div>

                    {/* Notice about stage names */}
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800/30 dark:bg-blue-900/20 dark:text-blue-400">
                        <div className="flex items-start gap-2">
                            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                            <span>
                                <strong>توجه:</strong> نام مراحل در این بخش قابل ویرایش نیست.
                                برای تغییر نام مراحل، لطفاً از صفحه <strong>ایجاد قیف آماده</strong> اقدام کنید.
                            </span>
                        </div>
                    </div>

                    {/* Stages */}
                    <div>
                        <div className="mb-4 flex items-center justify-between">
                            <h4 className="text-base font-bold text-zinc-900 dark:text-white">
                                مراحل قیف (۶ مرحله)
                            </h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                انتخاب معیار برای هر مرحله
                            </span>
                        </div>
                        <div className="space-y-3">
                            {stages.map((stage) => {
                                const availableCriteria = getCriteriaByStage(
                                    stage.stageOrder
                                );

                                const selectedCriterion = getSelectedCriterion(
                                    stage.stageOrder
                                );

                                const isChanged =
                                    changedStages[stage.stageOrder] !== undefined;

                                const isDetailsOpen =
                                    showCriterionDetails[stage.stageOrder] === true;

                                return (
                                    <div
                                        key={stage.stageOrder}
                                        className={`rounded-xl border transition ${isChanged
                                            ? "border-teal-300 bg-teal-50/50 dark:border-teal-700 dark:bg-teal-900/20"
                                            : "border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900/50"
                                            }`}
                                    >
                                        {/* ========================= */}
                                        {/* Stage Main Row */}
                                        {/* ========================= */}
                                        <div className="p-4">
                                            <div className="flex text-right flex-col gap-4 lg:flex-row lg:items-center">

                                                {/* ========================= */}
                                                {/* Stage Info */}
                                                {/* ========================= */}
                                                <div className="flex text-right items-center gap-3 w-full">

                                                    {/* Stage Number */}
                                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-teal-50 text-xs font-bold text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                                                        {getStageNumber(stage.stageOrder)}
                                                    </div>

                                                    {/* Stage Name */}
                                                    <div className="min-w-0 text-right">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                                                                {stage.stageName ||
                                                                    `مرحله ${stage.stageOrder}`}
                                                            </span>

                                                            {isChanged && (
                                                                <span className="text-[10px] font-medium text-teal-600 dark:text-teal-400">
                                                                    تغییر کرده
                                                                </span>
                                                            )}
                                                        </div>

                                                        <span className="mt-1 block text-xs text-gray-400 dark:text-gray-500">
                                                            مرحله {getStageNumber(stage.stageOrder)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* ========================= */}
                                                {/* Criterion Section */}
                                                {/* ========================= */}
                                                <div className="min-w-0 w-full flex-1">

                                                    <div className="flex w-full flex-row gap-2 sm:flex-row sm:items-end">

                                                        {/* Select */}
                                                        <div className="flex-1 w-2/8">
                                                            <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                                                                معیار مرحله
                                                            </label>

                                                            <select
                                                                value={stage.criterionId || ""}
                                                                onChange={(e) =>
                                                                    handleCriterionChange(
                                                                        stage.stageOrder,
                                                                        e.target.value
                                                                    )
                                                                }
                                                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:focus:border-teal-400"
                                                            >
                                                                <option value="">
                                                                    انتخاب معیار
                                                                </option>

                                                                {availableCriteria.map(
                                                                    (criterion) => (
                                                                        <option
                                                                            key={criterion.id}
                                                                            value={criterion.id}
                                                                        >
                                                                            {criterion.title}
                                                                        </option>
                                                                    )
                                                                )}
                                                            </select>
                                                        </div>

                                                        {/* Type + Details */}
                                                        <div className="flex flex-shrink-0 w-2/8 items-center gap-2">
                                                            {/* Details Button */}
                                                            {selectedCriterion && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        toggleCriterionDetails(
                                                                            stage.stageOrder
                                                                        )
                                                                    }
                                                                    className={`inline-flex h-[38px] items-center justify-center rounded-lg border px-3 text-xs font-medium transition ${isDetailsOpen
                                                                        ? "border-teal-300 bg-teal-50 text-teal-700 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-400"
                                                                        : "border-gray-200 bg-white text-gray-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-400 dark:hover:border-teal-700 dark:hover:bg-teal-900/20 dark:hover:text-teal-400"
                                                                        }`}
                                                                >
                                                                    {isDetailsOpen
                                                                        ? "بستن جزئیات"
                                                                        : "جزئیات معیار"}
                                                                </button>
                                                            )}
                                                            {/* Type */}
                                                            {selectedCriterion && (
                                                                <div>
                                                                    {getTypeBadge(
                                                                        selectedCriterion.type
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ========================= */}
                                        {/* Criterion Details */}
                                        {/* ========================= */}
                                        {isDetailsOpen && selectedCriterion && (
                                            <div className="border-t border-gray-200 px-4 pb-4 pt-0 dark:border-zinc-700">
                                                <CriterionDetails
                                                    criterion={selectedCriterion}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Notice */}
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800/30 dark:bg-amber-900/20 dark:text-amber-400">
                        <div className="flex items-start gap-2">
                            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                            <span>
                                تغییرات روی قیف‌های آماده اعمال می‌شود. کسب‌وکارهایی که قبلاً
                                این قیف را انتخاب کرده‌اند، تحت تأثیر قرار نمی‌گیرند.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 flex justify-end gap-3 border-t border-teal-100 bg-white px-6 py-4 dark:border-zinc-700 dark:bg-zinc-800">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:text-gray-400 dark:hover:bg-zinc-700/50"
                    >
                        انصراف
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-6 py-2 text-sm font-medium text-white transition hover:bg-teal-800 disabled:opacity-50 dark:bg-teal-600 dark:hover:bg-teal-700"
                    >
                        {loading ? (
                            <>
                                <svg
                                    className="h-4 w-4 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                                در حال ذخیره...
                            </>
                        ) : (
                            <>
                                <CheckIcon className="h-5 w-5" />
                                ذخیره تغییرات
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditTemplateModal;